// projectsView — how the author looks at All projects (pen "Projects and the
// Studio: in and out", All projects and its table scene), pure.
//
// What they chose: the sort (Last changed, Title), its order, and the view
// (Cards, Table). Both views share it. The host keeps it per device
// (projectsViewPreference.ts) and advances it only through `foldProjectsView`.
// A value this build does not know falls back to Last changed / Cards.

export type {
    ProjectsLayout,
    ProjectsOrder,
    ProjectsSort,
    ProjectsView,
    ProjectsViewCommand,
};
export {
    DEFAULT_PROJECTS_VIEW,
    foldProjectsView,
    matchesQuery,
    PROJECTS_SORTS,
    parseProjectsView,
    serializeProjectsView,
    sortProjects,
};

import { exhaustSwitchCase } from "@bilda/utils";

type ProjectsSort = "changed" | "title";
// desc: newest first (Last changed), Z to A (Title); asc the other way.
type ProjectsOrder = "desc" | "asc";
type ProjectsLayout = "cards" | "table";

type ProjectsView = {
    readonly sort: ProjectsSort;
    readonly order: ProjectsOrder;
    readonly layout: ProjectsLayout;
};

// One command per intent. A column heading sorts by its column; a second
// click on the sorted one reverses it.
type ProjectsViewCommand =
    | { readonly type: "sort-by"; readonly sort: ProjectsSort }
    | { readonly type: "order-by"; readonly order: ProjectsOrder }
    | { readonly type: "sort-by-column"; readonly sort: ProjectsSort }
    | { readonly type: "show-as"; readonly layout: ProjectsLayout };

const PROJECTS_SORTS: readonly ProjectsSort[] = ["changed", "title"];
const ORDERS: readonly ProjectsOrder[] = ["desc", "asc"];
const LAYOUTS: readonly ProjectsLayout[] = ["cards", "table"];

const DEFAULT_PROJECTS_VIEW: ProjectsView = {
    sort: "changed",
    order: "desc",
    layout: "cards",
};

// Each sort's natural first order: newest first; A to Z.
const firstOrder = (sort: ProjectsSort): ProjectsOrder => {
    switch (sort) {
        case "changed":
            return "desc";
        case "title":
            return "asc";
        default:
            return exhaustSwitchCase(sort);
    }
};

const foldProjectsView = (
    view: ProjectsView,
    cmd: ProjectsViewCommand,
): ProjectsView => {
    switch (cmd.type) {
        case "sort-by":
            return view.sort === cmd.sort
                ? view
                : { ...view, sort: cmd.sort, order: firstOrder(cmd.sort) };
        case "order-by":
            return view.order === cmd.order
                ? view
                : { ...view, order: cmd.order };
        case "sort-by-column":
            return view.sort === cmd.sort
                ? { ...view, order: view.order === "asc" ? "desc" : "asc" }
                : { ...view, sort: cmd.sort, order: firstOrder(cmd.sort) };
        case "show-as":
            return view.layout === cmd.layout
                ? view
                : { ...view, layout: cmd.layout };
        default:
            return exhaustSwitchCase(cmd);
    }
};

type Sortable = {
    readonly id: string;
    readonly title: string;
    // ISO 8601; absent sorts last in either order.
    readonly changedAt?: string;
};

// Sorted per the view; ties keep the given order.
const sortProjects = <T extends Sortable>(
    items: readonly T[],
    view: ProjectsView,
    locale: string,
): readonly T[] => {
    const sign = view.order === "asc" ? 1 : -1;
    const compare = (a: T, b: T): number => {
        switch (view.sort) {
            case "changed":
                if (a.changedAt === b.changedAt) return 0;
                if (a.changedAt === undefined) return 1;
                if (b.changedAt === undefined) return -1;
                return sign * a.changedAt.localeCompare(b.changedAt);
            case "title":
                return (
                    sign *
                    a.title.localeCompare(b.title, locale, {
                        sensitivity: "base",
                    })
                );
            default:
                return exhaustSwitchCase(view.sort);
        }
    };
    return [...items].sort(compare);
};

// The search field: every word of the query in the title, any case or accent.
const matchesQuery = (
    title: string,
    query: string,
    locale: string,
): boolean => {
    const fold = (s: string) =>
        s.normalize("NFD").replace(/\p{M}/gu, "").toLocaleLowerCase(locale);
    const hay = fold(title);
    return fold(query)
        .split(/\s+/u)
        .filter((w) => w !== "")
        .every((w) => hay.includes(w));
};

// === Persistence (parse, don't validate) ====================================

const isRecord = (v: unknown): v is Record<string, unknown> =>
    typeof v === "object" && v !== null && !Array.isArray(v);

const parseJson = (raw: string): unknown => {
    try {
        return JSON.parse(raw);
    } catch {
        return null;
    }
};

const parseProjectsView = (raw: string | null): ProjectsView => {
    const data = raw === null ? null : parseJson(raw);
    if (!isRecord(data)) return DEFAULT_PROJECTS_VIEW;
    return {
        sort:
            PROJECTS_SORTS.find((s) => s === data.sort) ??
            DEFAULT_PROJECTS_VIEW.sort,
        order:
            ORDERS.find((o) => o === data.order) ?? DEFAULT_PROJECTS_VIEW.order,
        layout:
            LAYOUTS.find((l) => l === data.layout) ??
            DEFAULT_PROJECTS_VIEW.layout,
    };
};

const serializeProjectsView = (view: ProjectsView): string =>
    JSON.stringify(view);
